import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogOut, ArrowLeft, Trash2, Eye, EyeOff, Plus, X, Pencil } from "lucide-react";

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
}

const inputCls =
  "rounded-none bg-mtk-bg border border-mtk-border text-white placeholder:text-zinc-600 focus-visible:ring-1 focus-visible:ring-mtk-gold focus-visible:ring-offset-0 focus-visible:border-mtk-gold/60";
const labelCls = "font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-400";

const EMPTY_FORM = { title: "", excerpt: "", content: "", author: "Xoel Crespo", published: false };

export default function AdminBlog() {
  const { t, lang, toggle } = useLang();
  const { admin, logout } = useAdminAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null); // null = crear, post = editar
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/blog");
      setPosts(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const openCreate = () => {
    setEditingPost(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setForm({
      title: post.title,
      excerpt: post.excerpt || "",
      content: post.content,
      author: post.author,
      published: post.published,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingPost(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (editingPost) {
        await api.patch(`/admin/blog/${editingPost.id}`, form);
        toast.success("Artículo actualizado");
      } else {
        await api.post("/admin/blog", form);
        toast.success("Artículo creado");
      }
      closeForm();
      fetchPosts();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (post) => {
    try {
      await api.patch(`/admin/blog/${post.id}`, { published: !post.published });
      fetchPosts();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = (post) => {
    toast("¿Eliminar este artículo?", {
      description: post.title,
      action: {
        label: "Eliminar",
        onClick: async () => {
          try {
            await api.delete(`/admin/blog/${post.id}`);
            toast.success("Artículo eliminado");
            fetchPosts();
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
          }
        },
      },
      cancel: {
        label: "Cancelar",
        onClick: () => {},
      },
    });
  };

  return (
    <div className="min-h-screen bg-mtk-bg text-white flex flex-col" data-testid="admin-blog-page">
      {/* Header */}
      <header className="border-b border-mtk-border bg-mtk-bg/95 backdrop-blur-xl sticky top-0 z-30">
        <div className="px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-baseline gap-2 text-zinc-400 hover:text-mtk-gold transition-colors" data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <Link to="/" className="flex items-baseline gap-2">
              <span className="font-heading text-lg font-black tracking-[0.2em] text-white">MTK</span>
              <span className="font-mono-mtk text-[10px] tracking-[0.3em] text-mtk-gold uppercase">ADMIN</span>
            </Link>
            <span className="hidden md:inline-block text-zinc-700">/</span>
            <span className="hidden md:inline-block font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-500">
              {t.blog.manageBlog}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-block font-mono-mtk text-[10px] tracking-[0.2em] text-zinc-500">
              {admin?.email}
            </span>
            <button
              onClick={toggle}
              className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-400 hover:text-mtk-gold transition-colors border border-white/10 hover:border-mtk-gold/40 px-3 py-2"
            >
              <span className={lang === "es" ? "text-mtk-gold" : ""}>ES</span>
              <span className="mx-1 text-white/20">/</span>
              <span className={lang === "en" ? "text-mtk-gold" : ""}>EN</span>
            </button>
            <Button
              onClick={logout}
              variant="outline"
              className="rounded-none border-mtk-border bg-transparent text-zinc-300 hover:bg-white/5 hover:text-mtk-gold font-mono-mtk text-[10px] tracking-[0.3em] uppercase h-10 px-4"
            >
              <LogOut className="w-3.5 h-3.5 mr-2" />
              {t.admin.logout}
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 px-6 md:px-10 py-8 md:py-10 max-w-5xl mx-auto w-full">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading text-2xl font-black uppercase tracking-tight text-white">
            {t.blog.manageBlog}
          </h1>
          {!showForm && (
            <Button
              onClick={openCreate}
              className="rounded-none bg-mtk-gold text-mtk-bg hover:bg-mtk-gold-light font-bold uppercase tracking-[0.2em] text-xs h-10 px-5"
            >
              <Plus className="w-3.5 h-3.5 mr-2" />
              {t.blog.newPost}
            </Button>
          )}
        </div>

        {/* Create / Edit form */}
        {showForm && (
          <div className="border border-mtk-gold/40 bg-mtk-surface p-6 md:p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <span className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-mtk-gold">
                {editingPost ? "EDITAR BLOG" : t.blog.newPost}
              </span>
              <button onClick={closeForm} className="text-zinc-500 hover:text-white transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label className={labelCls}>{t.blog.titleLabel}</Label>
                <Input
                  required
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  className={inputCls}
                  placeholder="LA CLAVE DE LA SALUD MASCULINA"
                />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>{t.blog.excerptLabel}</Label>
                <Input
                  value={form.excerpt}
                  onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
                  className={inputCls}
                  placeholder="Descripción corta del artículo…"
                />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>{t.blog.contentLabel}</Label>
                <Textarea
                  required
                  value={form.content}
                  onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                  className={`${inputCls} min-h-[320px] resize-y`}
                  placeholder="Escribe el contenido del artículo aquí…"
                />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>{t.blog.authorLabel}</Label>
                <Input
                  value={form.author}
                  onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))}
                  className={inputCls}
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="published"
                  checked={form.published}
                  onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
                  className="accent-mtk-gold w-4 h-4"
                />
                <Label htmlFor="published" className={labelCls}>
                  {t.blog.publish} (visible en /blog)
                </Label>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <Button
                  type="submit"
                  disabled={saving}
                  className="rounded-none bg-mtk-gold text-mtk-bg hover:bg-mtk-gold-light font-bold uppercase tracking-[0.2em] text-xs h-11 px-8 disabled:opacity-60"
                >
                  {saving ? t.blog.saving : t.blog.save}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeForm}
                  className="rounded-none border-mtk-border bg-transparent text-zinc-400 hover:text-white font-mono-mtk text-[10px] tracking-[0.3em] uppercase h-11 px-6"
                >
                  {t.blog.cancel}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Posts table */}
        {loading && (
          <div className="font-mono-mtk text-[10px] tracking-[0.3em] text-zinc-600 uppercase py-10">
            LOADING…
          </div>
        )}

        {!loading && posts.length === 0 && (
          <div className="border border-mtk-border py-16 text-center">
            <p className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-600">
              {t.blog.noBlogPosts}
            </p>
          </div>
        )}

        {!loading && posts.length > 0 && (
          <div className="border border-mtk-border">
            <table className="w-full">
              <thead>
                <tr className="border-b border-mtk-border bg-mtk-surface">
                  <th className="text-left px-5 py-3 font-mono-mtk text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                    Título
                  </th>
                  <th className="text-left px-4 py-3 font-mono-mtk text-[10px] tracking-[0.2em] uppercase text-zinc-500 hidden md:table-cell">
                    Fecha
                  </th>
                  <th className="text-left px-4 py-3 font-mono-mtk text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                    Estado
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {posts.map((post) => (
                  <tr key={post.id} className="border-b border-mtk-border last:border-0 hover:bg-mtk-surface/50 transition-colors">
                    <td className="px-5 py-4">
                      <button
                        onClick={() => openEdit(post)}
                        className="text-white text-sm font-medium line-clamp-1 hover:text-mtk-gold transition-colors text-left"
                      >
                        {post.title}
                      </button>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      <span className="font-mono-mtk text-[10px] tracking-[0.2em] text-zinc-500">
                        {formatDate(post.created_at)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center px-2.5 py-1 border font-mono-mtk text-[10px] tracking-[0.2em] uppercase rounded-none ${
                        post.published
                          ? "bg-mtk-gold/10 text-mtk-gold border-mtk-gold/40"
                          : "bg-mtk-bg text-zinc-500 border-mtk-border"
                      }`}>
                        {post.published ? t.blog.published : t.blog.draft}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => openEdit(post)}
                          title="Editar"
                          className="text-zinc-400 hover:text-white p-1.5 transition-colors"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => togglePublish(post)}
                          title={post.published ? t.blog.unpublish : t.blog.publish}
                          className="text-zinc-400 hover:text-mtk-gold p-1.5 transition-colors"
                        >
                          {post.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => remove(post)}
                          className="text-zinc-400 hover:text-red-400 p-1.5 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
